import { Box, Button, Grid, IconButton, InputBase, List, ListItem, ListItemText, Typography, useMediaQuery } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  addMonths,
  startOfDay,
  isAfter,
  isValid,
} from 'date-fns';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';


export const isFutureDate = (date: Date) => {
  const today = new Date();
  const input = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const current = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return input > current;
};

export const dateRangeSchema = z
  .object({
    startDate: z.date({ message: 'Enter valid date' }),
    endDate: z.date({ message: 'Enter valid date' }),
  })
  .superRefine((data, ctx) => {
    const startDateOnly = new Date(
      data.startDate.getFullYear(),
      data.startDate.getMonth(),
      data.startDate.getDate()
    );
    const endDateOnly = new Date(
      data.endDate.getFullYear(),
      data.endDate.getMonth(),
      data.endDate.getDate()
    );

    if (isFutureDate(startDateOnly)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Date must not be in the future',
      });
    }

    if (isFutureDate(endDateOnly)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Date must not be in the future',
      });
    }

    if (endDateOnly < startDateOnly) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date cannot be before start date',
      });
    }

    if (endDateOnly.getTime() === startDateOnly.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Start date and end date cannot be the same',
      });
    }
  });

export type DateRangeFormValues = {
  startDate: Date;
  endDate: Date;
  [key: string]: any;
};

export const PRESETS = [
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 3 months', value: 'last_3_months' },
  { label: 'Last 6 months', value: 'last_6_months' },
  { label: 'Last 12 months', value: 'last_12_months' },
];

export const getPresetRange = (value: string, siteDetails): { start: Date; end: Date } => {
  const today = startOfDay(new Date());
  switch (value) {
    case 'last_7_days':
      return { start: subDays(today, 6), end: today };
    case 'last_28_days':
      return { start: subDays(today, 27), end: today };
    case 'last_30_days':
      return { start: subDays(today, 29), end: today };
    case 'last_3_months':
      return { start: subMonths(today, 3), end: today };
    case 'last_6_months':
      return { start: subMonths(today, 6), end: today };
    case 'last_12_months':
      return { start: subMonths(today, 12), end: today };
    case 'since_seoSpace':
      return { start: startOfDay(siteDetails?.created_at), end: today };
    default:
      return { start: today, end: today };
  }
};

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Styles for each individual segment input (MM, DD, YYYY) */
const segmentInputSx = {
  fontFamily: 'Inter',
  fontSize: '13px',
  color: 'var(--color-text-darkest)',
  fontWeight: 500,
  lineHeight: 'var(--line-height-20)',
  '& input': {
    padding: 0,
    textAlign: 'center' as const,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 'var(--line-height-20)',
    color: 'var(--color-text-darkest)',
    '&::placeholder': {
      color: 'var(--color-text-placeholder)',
      opacity: 1,
      fontWeight: 400,
    },
  },
};

const slashSx = {
  fontFamily: 'Inter',
  fontSize: '13px',
  color: 'var(--color-text-placeholder)',
  fontWeight: 400,
  userSelect: 'none' as const,
  lineHeight: 'var(--line-height-20)',
};

const inputLabelSx = {
  fontFamily: 'Inter',
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--color-text-dark)',
  lineHeight: 'var(--line-height-16)',
  // mb: 0.5,
};

type DateSegments = { mm: string; dd: string; yyyy: string };

const dateToSegments = (date: Date | null): DateSegments => {
  if (!date) return { mm: '', dd: '', yyyy: '' };
  return {
    mm: format(date, 'MM'),
    dd: format(date, 'dd'),
    yyyy: format(date, 'yyyy'),
  };
};

const segmentsToDate = (seg: DateSegments): Date | null => {
  if (!seg.mm || !seg.dd || !seg.yyyy) return null;
  const m = parseInt(seg.mm, 10);
  const d = parseInt(seg.dd, 10);
  const y = parseInt(seg.yyyy, 10);
  if (isNaN(m) || isNaN(d) || isNaN(y)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return null;
  const parsed = new Date(y, m - 1, d);
  if (!isValid(parsed) || parsed.getMonth() !== m - 1) return null;
  if (isFutureDate(parsed)) return null;
  return startOfDay(parsed);
};

export type DateRangePickerProps<TSchema = unknown> = {
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  presets?: Array<{ label: string; value: string | number }>;
  onApply: (startDate: Date, endDate: Date, preset: string | number) => void;
  onCancel: () => void;
  /** Custom Zod schema for validating { startDate, endDate }. Defaults to dateRangeSchema. */
  schema?: TSchema;
  /** Callback to determine whether a date should be disabled (un-clickable). Defaults to isFutureDate. */
  isDateDisabled?: (date: Date) => boolean;
  /** Resolver that maps a preset value to a { start, end } date range. Defaults to built-in getPresetRange. */
  resolvePresetRange?: (value: string | number) => { start: Date; end: Date };
};

const DateRangePicker = <TSchema,>({
  initialStartDate = null,
  initialEndDate = null,
  presets = PRESETS,
  onApply,
  onCancel,
  schema: schemaProp,
  isDateDisabled = isFutureDate,
  resolvePresetRange: resolvePresetRangeProp,
}: DateRangePickerProps<TSchema>) => {

  // Resolve schema: use provided schema or fall back to default dateRangeSchema
  const schema = schemaProp ?? dateRangeSchema;

  // Below 820px: move presets above calendars, buttons below
  const isCompact = useMediaQuery('(max-width:819px)');
  // Below 620px: show single calendar instead of two side-by-side
  const isMobile = useMediaQuery('(max-width:619px)');

  const userDetails = useSelector(
      (state: RootState) => state.counter.userDetails
    );

  // Use the custom preset resolver if provided, otherwise fall back to the built-in getPresetRange
  const resolvePresetRange = resolvePresetRangeProp ?? ((value: string) => getPresetRange(value, userDetails));

  // Local version of segmentsToDate that respects the isDateDisabled prop
  const segmentsToDateLocal = useCallback((seg: DateSegments): Date | null => {
    if (!seg.mm || !seg.dd || !seg.yyyy) return null;
    const m = parseInt(seg.mm, 10);
    const d = parseInt(seg.dd, 10);
    const y = parseInt(seg.yyyy, 10);
    if (isNaN(m) || isNaN(d) || isNaN(y)) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return null;
    const parsed = new Date(y, m - 1, d);
    if (!isValid(parsed) || parsed.getMonth() !== m - 1) return null;
    if (isDateDisabled(parsed)) return null;
    return startOfDay(parsed);
  }, [isDateDisabled]);

  const findInitialPreset = () => {
    if (!initialStartDate || !initialEndDate) return '';
    const match = presets.find((p) => {
      const r = resolvePresetRange(p.value);
      return isSameDay(r.start, initialStartDate) && isSameDay(r.end, initialEndDate);
    });
    return match?.value ?? '';
  };

  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    initialStartDate ? startOfMonth(initialStartDate) : startOfMonth(subMonths(new Date(), 1))
  );
  const [selectedPreset, setSelectedPreset] = useState<string | number>(findInitialPreset);

  // Segmented date input state
  const [startSegs, setStartSegs] = useState<DateSegments>(dateToSegments(initialStartDate));
  const [endSegs, setEndSegs] = useState<DateSegments>(dateToSegments(initialEndDate));
  const [startFocused, setStartFocused] = useState(false);
  const [endFocused, setEndFocused] = useState(false);

  // Refs for each segment: start (mm, dd, yyyy) + end (mm, dd, yyyy)
  const startMmRef = useRef<HTMLInputElement>(null);
  const startDdRef = useRef<HTMLInputElement>(null);
  const startYyyyRef = useRef<HTMLInputElement>(null);
  const endMmRef = useRef<HTMLInputElement>(null);
  const endDdRef = useRef<HTMLInputElement>(null);
  const endYyyyRef = useRef<HTMLInputElement>(null);

  // Sync segment values when dates change from calendar clicks / presets
  useEffect(() => {
    if (!startFocused) {
      setStartSegs(dateToSegments(startDate));
    }
  }, [startDate, startFocused]);

  useEffect(() => {
    if (!endFocused) {
      setEndSegs(dateToSegments(endDate));
    }
  }, [endDate, endFocused]);

  // Try to apply a segmented date when a full value is typed
  const tryApplyStart = useCallback(
    (segs: DateSegments) => {
      const parsed = segmentsToDateLocal(segs);
      if (parsed) {
        setStartDate(parsed);
        setValue('startDate', parsed);
        setCurrentMonth(startOfMonth(parsed));
        setSelectedPreset('');
        if (endDate) trigger(['startDate', 'endDate']);
      }
    },
    [endDate]
  );

  const tryApplyEnd = useCallback(
    (segs: DateSegments) => {
      const parsed = segmentsToDateLocal(segs);
      if (parsed) {
        setEndDate(parsed);
        setValue('endDate', parsed);
        setSelectedPreset('');
        if (startDate) trigger(['startDate', 'endDate']);
      }
    },
    [startDate]
  );

  // Generic segment change handler
  const handleSegmentChange = (
    value: string,
    maxLen: number,
    setter: React.Dispatch<React.SetStateAction<DateSegments>>,
    field: keyof DateSegments,
    nextRef: React.RefObject<HTMLInputElement> | null,
    applyFn: (segs: DateSegments) => void
  ) => {
    const digits = value.replace(/\D/g, '').slice(0, maxLen);
    setter((prev) => {
      const updated = { ...prev, [field]: digits };
      // Auto-advance to next segment when full
      if (digits.length === maxLen && nextRef?.current) {
        setTimeout(() => nextRef.current?.focus(), 0);
      }
      // Try to apply the complete date
      if (digits.length === maxLen) {
        setTimeout(() => applyFn(updated), 0);
      }
      return updated;
    });
  };

  // Backspace handler: if segment is empty, go to previous segment
  const handleSegmentKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentValue: string,
    prevRef: React.RefObject<HTMLInputElement> | null,
    nextRef: React.RefObject<HTMLInputElement> | null,
    maxLen: number
  ) => {
    if (e.key === 'Backspace' && currentValue === '' && prevRef?.current) {
      e.preventDefault();
      prevRef.current.focus();
    }
    if (e.key === 'ArrowRight' && nextRef?.current) {
      const input = e.target as HTMLInputElement;
      if (input.selectionStart === currentValue.length) {
        e.preventDefault();
        nextRef.current.focus();
      }
    }
    if (e.key === 'ArrowLeft' && prevRef?.current) {
      const input = e.target as HTMLInputElement;
      if (input.selectionStart === 0) {
        e.preventDefault();
        prevRef.current.focus();
      }
    }
    // Tab forward from last start segment → first end segment
    if (e.key === 'Tab' && !e.shiftKey && nextRef === null && endMmRef.current) {
      // do nothing, let natural tab flow
    }
  };

  // Focus tracking via container-level focus/blur
  const handleStartContainerFocus = () => setStartFocused(true);
  const handleStartContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Only mark unfocused if focus is leaving the container entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setStartFocused(false);
    }
  };
  const handleEndContainerFocus = () => setEndFocused(true);
  const handleEndContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setEndFocused(false);
    }
  };

  const { handleSubmit, setValue, trigger, formState } = useForm<Record<string, any>>({
    ...(schema ? { resolver: zodResolver(schema as never) } : {}),
    mode: 'onChange',
    defaultValues: {
      startDate: initialStartDate ?? (null as any),
      endDate: initialEndDate ?? (null as any),
    },
  });

  const hasErrors = Object.keys(formState.errors).length > 0;
  const hasNoDatesSelected = !startDate || !endDate;
  const today = startOfDay(new Date());

  const handleDateClick = (day: Date) => {
    if (isDateDisabled(startOfDay(day))) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(day);
      setEndDate(null);
      setSelectedPreset('');
      setValue('startDate', day);
      setValue('endDate', null as any);
    } else if (isSameDay(day, startDate)) {
      return;
    } else if (isAfter(startDate, day)) {
      setStartDate(day);
      setEndDate(null);
      setSelectedPreset('');
      setValue('startDate', day);
      setValue('endDate', null as any);
    } else {
      setEndDate(day);
      setValue('endDate', day);
      trigger(['startDate', 'endDate']);
    }
  };

  const handlePresetClick = (presetValue: string | number) => {
    const range = resolvePresetRange(presetValue);
    setSelectedPreset(presetValue);
    setStartDate(range.start);
    setEndDate(range.end);
    setCurrentMonth(startOfMonth(subMonths(range.end, 1)));
    setValue('startDate', range.start);
    setValue('endDate', range.end);
    trigger(['startDate', 'endDate']);
  };

  const effectiveEnd =
    endDate || (startDate && hoverDate && isAfter(hoverDate, startDate) ? hoverDate : null);

  const renderCalendar = (monthDate: Date, monthType: 'start' | 'end') => {
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 }),
      end: endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 }),
    });

    return (
      <Box sx={{ width: isMobile ? '100%' : isCompact ? '50%' : '280px', minWidth: 0, ...(monthType === 'start' ? { pr: isMobile ? 0 : 1 } : { pl: isMobile ? 0 : 1 }) }}>
        <Box
          sx={{
            py: 2,
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              flex: 1,
              textAlign: 'center',
              fontWeight: 600,
              color: 'var(--color-text-dark)',
              fontSize: 'var(--font-size-14)',
              fontFamily: 'Inter',
              lineHeight: 'var(--line-height-20)',
            }}
          >
            {format(monthDate, 'MMMM yyyy')}
          </Typography>
          
          {monthType === 'start' && (
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <IconButton size="small" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

            </Box>
          )}

          {monthType === 'end' && (
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  disabled={isNextDisabled}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>

            </Box>
          )}
        </Box>

        <Grid container columns={7} sx={{ mb: 0.5 }}>
          {DAY_LABELS.map((d) => (
            <Grid item xs={1} key={d} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '12px', color: 'var(--color-text-mid)', fontWeight: 500, fontFamily: 'Inter', lineHeight: 'var(--line-height-18)' }}>
                {d}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container columns={7} sx={{ rowGap: '4px' }}>
          {days.map((day, idx) => {
            const isDayDisabled = isDateDisabled(startOfDay(day));
            const isCurrentMonth = isSameMonth(day, monthDate);
            const isDayToday = isSameDay(day, new Date());

            const dayStart = startOfDay(day);
            const isStart = startDate && isSameDay(dayStart, startDate);
            const isEnd = endDate && isSameDay(dayStart, endDate);

            const inConfirmedRange =
              startDate && endDate
                ? (() => {
                    const s = startOfDay(startDate);
                    const e = startOfDay(endDate);
                    return s <= e
                      ? isWithinInterval(dayStart, { start: s, end: e })
                      : false;
                  })()
                : false;

            const inHoverRange =
              !inConfirmedRange && startDate && effectiveEnd && !endDate
                ? (() => {
                    const s = startOfDay(startDate);
                    const e = startOfDay(effectiveEnd);
                    const intervalStart = s <= e ? s : e;
                    const intervalEnd = s <= e ? e : s;
                    return isWithinInterval(dayStart, { start: intervalStart, end: intervalEnd });
                  })()
                : false;

            // Dates strictly between start and end (not the endpoints)
            const isBetween = inConfirmedRange && !isStart && !isEnd;

            // Whether this cell should show the continuous band background
            const showBand = isCurrentMonth && (isBetween || (isStart && endDate) || (isEnd && startDate));
            const showHoverBand = isCurrentMonth && inHoverRange;

            // Position in the week row (0=Sun .. 6=Sat)
            const colIndex = idx % 7;
            const isRowStart = colIndex === 0;
            const isRowEnd = colIndex === 6;

            // Band background on the outer cell (continuous, edge-to-edge)
            let cellBg = 'transparent';
            let roundLeft = false;
            let roundRight = false;

            if (showBand) {
              cellBg = 'rgba(18, 2, 66, 0.08)';
              // Round on range endpoints
              if (isStart) roundLeft = true;
              if (isEnd) roundRight = true;
              // Round on row edges for multi-row ranges
              if (isRowStart && !isStart) roundLeft = true;
              if (isRowEnd && !isEnd) roundRight = true;
            } else if (showHoverBand) {
              cellBg = 'rgba(18, 2, 66, 0.05)';
              const isHoverStart = startDate && isSameDay(dayStart, startDate);
              const isHoverEnd = effectiveEnd && isSameDay(dayStart, effectiveEnd);
              if (isHoverStart) roundLeft = true;
              if (isHoverEnd) roundRight = true;
              if (isRowStart && !isHoverStart) roundLeft = true;
              if (isRowEnd && !isHoverEnd) roundRight = true;
            }

            const cellBorderRadius =
              roundLeft && roundRight ? '50%' :
              roundLeft ? '50% 0 0 50%' :
              roundRight ? '0 50% 50% 0' :
              '0px';

            // Inner circle styles (solid for start/end, transparent for others)
            let circleBg = 'transparent';
            let textColor = isCurrentMonth && !isDayDisabled ? 'var(--color-text-darkest)' : '#757a83';
            let cursor = isDayDisabled || !isCurrentMonth ? 'default' : 'pointer';

            if ((isStart || isEnd) && isCurrentMonth) {
              circleBg = 'var(--color-primary)';
              textColor = 'var(--color-white)';
            } else if (isBetween && isCurrentMonth) {
              textColor = 'var(--color-primary)';
            } else if (inHoverRange && isCurrentMonth) {
              textColor = 'var(--color-primary)';
            } else if (isDayToday && !inConfirmedRange && !isDayDisabled) {
              textColor = isCurrentMonth ? 'var(--color-primary)' : '#757a83';
            }

            return (
              <Grid
                item
                xs={1}
                key={idx}
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 0.25,
                  cursor,
                  backgroundColor: cellBg,
                  borderRadius: cellBorderRadius,
                  transition: 'background-color 0.15s ease',
                }}
                onClick={() => !isDayDisabled && isCurrentMonth && handleDateClick(day)}
                onMouseEnter={() => {
                  if (startDate && !endDate && !isDayDisabled && isCurrentMonth) {
                    setHoverDate(day);
                  }
                }}
                onMouseLeave={() => setHoverDate(null)}
              >
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: circleBg,
                    borderRadius: '50%',
                    transition: 'background-color 0.15s ease',
                    ...(!isDayDisabled && isCurrentMonth && !inConfirmedRange && !isStart && !isEnd && {
                      '&:hover': { backgroundColor: 'rgba(18, 2, 66, 0.08)' },
                    }),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: (isStart || isEnd) && isCurrentMonth ? 600 : 400,
                      color: textColor,
                      fontFamily: 'Inter',
                      lineHeight: 'var(--line-height-18)',
                    }}
                  >
                    {format(day, 'd')}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const isNextDisabled = isAfter(startOfMonth(addMonths(currentMonth, 2)), startOfMonth(today));

  const onSubmit = (data: any) => {
    onApply(data.startDate, data.endDate, selectedPreset ? selectedPreset : 'custom_date');
  };

  /** Renders a single segmented date input box, ordered by user date format */
  const isDDFirst = userDetails?.date_format === 'DD/MM/YYYY';

  const renderSegmentedInput = (
    segs: DateSegments,
    setter: React.Dispatch<React.SetStateAction<DateSegments>>,
    isFocused: boolean,
    dateValue: Date | null,
    mmRef: React.RefObject<HTMLInputElement>,
    ddRef: React.RefObject<HTMLInputElement>,
    yyyyRef: React.RefObject<HTMLInputElement>,
    onContainerFocus: () => void,
    onContainerBlur: (e: React.FocusEvent<HTMLDivElement>) => void,
    applyFn: (segs: DateSegments) => void,
    label: string
  ) => {
    // Determine segment order: first / second are the 2-digit fields, third is always YYYY
    const firstRef = isDDFirst ? ddRef : mmRef;
    const secondRef = isDDFirst ? mmRef : ddRef;

    const firstField: keyof DateSegments = isDDFirst ? 'dd' : 'mm';
    const secondField: keyof DateSegments = isDDFirst ? 'mm' : 'dd';
    const firstPlaceholder = isDDFirst ? 'DD' : 'MM';
    const secondPlaceholder = isDDFirst ? 'MM' : 'DD';
    const firstValue = isDDFirst ? segs.dd : segs.mm;
    const secondValue = isDDFirst ? segs.mm : segs.dd;

    return (
      <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center', maxWidth: 'max-content' }}>
        <Typography sx={inputLabelSx}>{label}</Typography>
        <Box
          onFocus={onContainerFocus}
          onBlur={onContainerBlur}
          onClick={() => firstRef.current?.focus()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: '7px',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: isFocused ? 'var(--color-primary)' : 'var(--color-border)',
            backgroundColor: 'var(--color-white)',
            cursor: 'text',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            boxShadow: isFocused ? '0 0 0 3px rgba(18, 2, 66, 0.08)' : 'none',
            '&:hover': {
              borderColor: isFocused ? 'var(--color-primary)' : 'var(--color-text-placeholder)',
            },
          }}
        >
          <CalendarTodayOutlinedIcon
            sx={{ fontSize: 'var(--font-size-15)', color: isFocused ? 'var(--color-primary)' : 'var(--color-text-tertiary)', flexShrink: 0, transition: 'color 0.15s ease' }}
          />
        {/* First segment (MM or DD based on format) */}
        <InputBase
          inputRef={firstRef}
          placeholder={firstPlaceholder}
          value={firstValue}
          onChange={(e) =>
            handleSegmentChange(e.target.value, 2, setter, firstField, secondRef, applyFn)
          }
          onKeyDown={(e) =>
            handleSegmentKeyDown(e as React.KeyboardEvent<HTMLInputElement>, firstValue, null, secondRef, 2)
          }
          onFocus={(e) => (e.target as HTMLInputElement).select()}
          sx={{
            ...segmentInputSx,
            width: '24px',
            '& input': { ...segmentInputSx['& input'], color: firstValue ? 'var(--color-text-darkest)' : undefined },
          }}
          inputProps={{ maxLength: 2, inputMode: 'numeric' }}
        />
        <Typography sx={slashSx}>/</Typography>
        {/* Second segment (DD or MM based on format) */}
        <InputBase
          inputRef={secondRef}
          placeholder={secondPlaceholder}
          value={secondValue}
          onChange={(e) =>
            handleSegmentChange(e.target.value, 2, setter, secondField, yyyyRef, applyFn)
          }
          onKeyDown={(e) =>
            handleSegmentKeyDown(e as React.KeyboardEvent<HTMLInputElement>, secondValue, firstRef, yyyyRef, 2)
          }
          onFocus={(e) => (e.target as HTMLInputElement).select()}
          sx={{
            ...segmentInputSx,
            width: '24px',
            '& input': { ...segmentInputSx['& input'], color: secondValue ? 'var(--color-text-darkest)' : undefined },
          }}
          inputProps={{ maxLength: 2, inputMode: 'numeric' }}
        />
        <Typography sx={slashSx}>/</Typography>
        {/* YYYY (always last) */}
        <InputBase
          inputRef={yyyyRef}
          placeholder="YYYY"
          value={segs.yyyy}
          onChange={(e) =>
            handleSegmentChange(e.target.value, 4, setter, 'yyyy', null, applyFn)
          }
          onKeyDown={(e) =>
            handleSegmentKeyDown(e as React.KeyboardEvent<HTMLInputElement>, segs.yyyy, secondRef, null, 4)
          }
          onFocus={(e) => (e.target as HTMLInputElement).select()}
          sx={{
            ...segmentInputSx,
            width: '40px',
            '& input': { ...segmentInputSx['& input'], color: segs.yyyy ? 'var(--color-text-darkest)' : undefined },
          }}
          inputProps={{ maxLength: 4, inputMode: 'numeric' }}
        />
        </Box>
      </Box>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Selected range display – segmented date inputs */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobile ? '10px' : '20px',
              padding: '20px 12px 0'
            }}
          >
            {/* Start date segmented input */}
            {renderSegmentedInput(
              startSegs,
              setStartSegs,
              startFocused,
              startDate,
              startMmRef,
              startDdRef,
              startYyyyRef,
              handleStartContainerFocus,
              handleStartContainerBlur,
              tryApplyStart,
              'Start date'
            )}

            {/* Dash separator */}
            {/* <Box sx={{ width: 12, height: '1px', backgroundColor: 'var(--color-border)', flexShrink: 0, mb: '18px' }} /> */}

            {/* End date segmented input */}
            {renderSegmentedInput(
              endSegs,
              setEndSegs,
              endFocused,
              endDate,
              endMmRef,
              endDdRef,
              endYyyyRef,
              handleEndContainerFocus,
              handleEndContainerBlur,
              tryApplyEnd,
              'End date'
            )}
          </Box>

            <Typography
              sx={{
                color: 'var(--color-error-red)',
                fontSize: '12px',
                fontFamily: 'Inter',
                fontWeight: 400,
                px: 2,
                marginTop: '6px',
                lineHeight: 'var(--line-height-16)',
              }}
            >
              {String(formState.errors.endDate?.message || formState.errors.startDate?.message || '')}
            </Typography>
        </Box>

        {/* Presets — horizontal row above calendars on compact screens */}
        {isCompact && (
          <Box sx={{ borderBottom: '1px solid var(--color-border-medium)', overflowX: 'auto', marginTop: '10px' }}>
            <List sx={{ p: 0, display: 'flex', flexWrap: 'nowrap' }}>
              {presets.map((preset) => (
                <ListItem
                  button
                  key={preset.value}
                  onClick={() => handlePresetClick(preset.value)}
                  selected={selectedPreset === preset.value}
                  sx={{
                    py: 0.875,
                    px: 2,
                    pl: 1.5,
                    width: 'auto',
                    flex: 'none',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-bg-muted)',
                      '&:hover': { backgroundColor: 'var(--color-border-medium)' },
                    },
                  }}
                >
                  <ListItemText
                    primary={preset.label}
                    primaryTypographyProps={{
                      fontSize: '13px',
                      lineHeight: 'var(--line-height-18)',
                      fontFamily: 'Inter',
                      fontWeight: selectedPreset === preset.value ? 600 : 500,
                      color: selectedPreset === preset.value ? 'var(--color-text-darkest)' : 'var(--color-text-mid)',
                      whiteSpace: 'nowrap',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: isCompact ? 'column' : 'row' }}>
          {/* Calendar panel */}
          <Box sx={{ flex: 1, borderRight: isCompact ? 'none' : '1px solid var(--color-border-medium)' }}>
            {/* Two calendars side by side (or stacked on mobile) */}
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', p: 1.5, pb: '20px', alignItems: isMobile ? 'center' : 'flex-start' }}>
              {renderCalendar(currentMonth, 'start')}
              {renderCalendar(addMonths(currentMonth, 1), 'end')}
            </Box>
          </Box>

          {/* Presets sidebar — only on larger screens */}
          {!isCompact && (
            <Box sx={{ width: '180px', display: 'flex', flexDirection: 'column' }}>
              <List sx={{ p: 0, flex: 1 }}>
                {presets.map((preset) => (
                  <ListItem
                    button
                    key={preset.value}
                    onClick={() => handlePresetClick(preset.value)}
                    selected={selectedPreset === preset.value}
                    sx={{
                      py: 0.875,
                      px: 2,
                      pl: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: 'var(--color-bg-muted)',
                        '&:hover': { backgroundColor: 'var(--color-border-medium)' },
                      },
                    }}
                  >
                    <ListItemText
                      primary={preset.label}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        lineHeight: 'var(--line-height-18)',
                        fontFamily: 'Inter',
                        fontWeight: selectedPreset === preset.value ? 600 : 500,
                        color: selectedPreset === preset.value ? 'var(--color-text-darkest)' : 'var(--color-text-mid)',
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* Action buttons — desktop sidebar */}
              <Box sx={{ p: 1.5, pt: 0.5, pb: '20px', pr: 1.5, display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="button"
                  onClick={onCancel}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    py: 1,
                    fontSize: '13px',
                    lineHeight: 'var(--line-height-18)',
                    fontFamily: 'Inter',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-dark)',
                    '&:hover': { borderColor: 'var(--color-text-placeholder)', backgroundColor: 'rgba(18, 2, 66, 0.04)' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  type="submit"
                  disabled={hasNoDatesSelected || hasErrors}
                  sx={{
                    backgroundColor: 'var(--color-primary)',
                    textTransform: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    py: 1,
                    fontSize: '13px',
                    lineHeight: 'var(--line-height-18)',
                    fontFamily: 'Inter',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#1a0360', boxShadow: 'none' },
                    '&.Mui-disabled': { backgroundColor: 'var(--color-bg-border-grey)', color: '#9AA0AB' },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Action buttons — below calendars on compact screens */}
        {isCompact && (
          <Box sx={{ p: 1.5, pt: 0.5, pb: '20px', display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: '1px solid var(--color-border-medium)' }}>
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={onCancel}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                py: 1,
                fontSize: '13px',
                lineHeight: 'var(--line-height-18)',
                fontFamily: 'Inter',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-dark)',
                minWidth: '100px',
                '&:hover': { borderColor: 'var(--color-text-placeholder)', backgroundColor: 'rgba(18, 2, 66, 0.04)' },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              type="submit"
              disabled={hasNoDatesSelected || hasErrors}
              sx={{
                backgroundColor: 'var(--color-primary)',
                textTransform: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                py: 1,
                fontSize: '13px',
                lineHeight: 'var(--line-height-18)',
                fontFamily: 'Inter',
                boxShadow: 'none',
                minWidth: '100px',
                '&:hover': { backgroundColor: '#1a0360', boxShadow: 'none' },
                '&.Mui-disabled': { backgroundColor: 'var(--color-bg-border-grey)', color: '#9AA0AB' },
              }}
            >
              Apply
            </Button>
          </Box>
        )}
      </Box>
    </form>
  );
};

export default DateRangePicker;
